import type { Festival } from "./festivals.js";

export interface ActiveFestival {
  festival: Festival;
  daysUntil: number;
}

const DAY_MS = 86_400_000;

/** Calendar days since the epoch. Built from UTC, so DST changes never shift a day. */
export const dayNum = (y: number, m: number, d: number): number => Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);

/** 'YYYY-MM-DD' to a day number, or null when the text is empty or malformed. */
export function parseYmd(s: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? dayNum(Number(m[1]), Number(m[2]), Number(m[3])) : null;
}

/**
 * The festival in its window (0 to windowDays days before it, the festival day included), or
 * null. The local calendar date decides. When two overlap, the nearer one wins.
 */
export function getActiveFestival(now: Date, list: readonly Festival[]): ActiveFestival | null {
  const year = now.getFullYear();
  const today = dayNum(year, now.getMonth() + 1, now.getDate());
  let best: ActiveFestival | null = null;
  for (const festival of list) {
    // Next year too, so the 1 January window starts on 25 December.
    for (const y of [year, year + 1]) {
      const occ = festival.fixed
        ? dayNum(y, festival.fixed.month, festival.fixed.day)
        : parseYmd(festival.dates?.[y] ?? "");
      if (occ == null) continue;
      const daysUntil = occ - today;
      if (daysUntil >= 0 && daysUntil <= festival.windowDays && (!best || daysUntil < best.daysUntil)) {
        best = { festival, daysUntil };
      }
    }
  }
  return best;
}

/** The line under the dashboard title: "Happy Diwali!" on the day, otherwise "Diwali is in 5 days." */
export function festiveLead({ festival, daysUntil }: ActiveFestival): string {
  if (daysUntil === 0) return `${festival.wish}!`;
  return `${festival.name} is ${daysUntil === 1 ? "tomorrow" : `in ${daysUntil} days`}.`;
}

/** The countdown part on its own: "Today", "Tomorrow" or "5 days to go". */
export function festiveWhen({ daysUntil }: ActiveFestival): string {
  return daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `${daysUntil} days to go`;
}

/** The badge tooltip and its aria-label: "Happy Diwali · 5 days to go". */
export function festiveTooltip(active: ActiveFestival): string {
  return `${active.festival.wish} · ${festiveWhen(active)}`;
}

/**
 * QA override from the URL: `?festival=diwali&days=5` forces that state, `?festival=off` forces
 * nothing. The days still go through the window, so `days=8` shows nothing. Returns null when
 * there is no `festival` parameter.
 */
export function festivalPreview(search: string, list: readonly Festival[]): { active: ActiveFestival | null } | null {
  const params = new URLSearchParams(search);
  const id = params.get("festival");
  if (id === null) return null;
  const festival = list.find((f) => f.id === id);
  const days = Number(params.get("days") ?? 0);
  if (!festival || !Number.isInteger(days) || days < 0 || days > festival.windowDays) return { active: null };
  return { active: { festival, daysUntil: days } };
}
